import { useEffect, useMemo, useState } from 'react';
import { systemService, type PluginSummary } from '../services/system.service.js';
import { useAuthStore } from '../stores/authStore.js';
import type { PageSpec } from '@elmes/shared/plugin/manifest';

/**
 * Numeric role hierarchy used for sidebar module visibility.
 * Higher value = more privilege. A user sees any module whose
 * requiredRole level is <= their own level.
 */
export const ROLE_HIERARCHY: Record<string, number> = {
  guest: 1,
  user: 2,
  designer: 3,
  manager: 4,
  admin: 5,
};

/**
 * Returns true if the user's role satisfies the required role level.
 * An unknown requiredRole denies (fail-closed, gh#3105): a typo or a role the hierarchy
 * has not learned must never open the gate. Exported for sidebar per-page filtering (#335).
 */
export function canAccessRole(requiredRole: string | undefined, userRole: string): boolean {
  if (!requiredRole) return true;
  if (!Object.hasOwn(ROLE_HIERARCHY, requiredRole)) return false;
  const reqLevel = ROLE_HIERARCHY[requiredRole];
  const userLevel = Object.hasOwn(ROLE_HIERARCHY, userRole) ? ROLE_HIERARCHY[userRole] : 0;
  return userLevel >= reqLevel;
}

/**
 * Module entries sourced from /api/system/modules (manifest-driven, issue #322).
 * Single source of truth: description is read from manifest (plugins.manifestJson),
 * not from atlas projection metadata. Atlas description is kept in sync as a
 * query optimization shadow via syncManifestToAtlas.
 */
export interface ModuleEntry {
  slug: string;
  title: string;
  /** Description from manifest — single source of truth (issue #322). */
  description: string;
  routePath: string;
  iconName: string;
  requiredRole?: string;
  /** Permission slugs the user must hold to access this module (#98). */
  requiredPermissions?: string[];
  showOnDashboard: boolean;
  dashboardOrder: number;
  /** ISO string from plugins table updatedAt */
  updatedAt: string;
  /** Lifecycle status: active | registered | disabled | error */
  status: string;
  version: string;
  category: string;
  /** Pages this plugin registers for dynamic routing (Faz 6, #183). */
  pages: PageSpec[];
}

interface UseModulesOptions {
  /** Apply current user's role filter. Defaults to true. */
  filterByRole?: boolean;
  /**
   * Apply current user's permission filter against module.requiredPermissions
   * (#98). Defaults to true. Admin role automatically passes everything.
   */
  filterByPermissions?: boolean;
  /** Restrict to modules whose metadata.showOnDashboard === true. */
  dashboardOnly?: boolean;
  /**
   * If false, the hook short-circuits: no fetch, returns empty modules with
   * loading=false. Used to skip plugin fetch on unauthenticated routes
   * (e.g. /login) and prevent 401 → hard-reload loops. Defaults to true.
   */
  enabled?: boolean;
}

interface UseModulesResult {
  modules: ModuleEntry[];
  loading: boolean;
  error: string | null;
}

/**
 * Map a PluginSummary (from /api/system/modules) to a ModuleEntry.
 * routePath is required for rendering; skip silently if absent.
 * iconName falls back to 'Box' when absent — tolerates manifest sync gaps
 * where backend strips icon field before writing to DB (gh#491).
 */
function pluginToModule(p: PluginSummary): ModuleEntry | null {
  if (!p.routePath) return null;
  return {
    slug: p.slug,
    title: p.name,
    description: p.description,
    routePath: p.routePath,
    iconName: p.iconName ?? 'Box',
    requiredRole: p.requiredRole ?? undefined,
    requiredPermissions: p.requiredPermissions.length > 0 ? p.requiredPermissions : undefined,
    showOnDashboard: p.showOnDashboard,
    dashboardOrder: p.dashboardOrder ?? 999,
    updatedAt: p.updatedAt,
    status: p.status,
    version: p.version,
    category: p.category,
    pages: p.pages ?? [],
  };
}

export function useModules(options: UseModulesOptions = {}): UseModulesResult {
  const { filterByRole = true, filterByPermissions = true, dashboardOnly = false, enabled = true } = options;
  const role = useAuthStore((s) => s.user?.role);
  const userPerms = useAuthStore((s) => s.user?.permissions);
  const [plugins, setPlugins] = useState<PluginSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setPlugins([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    systemService
      .listPlugins()
      .then((res) => {
        if (cancelled) return;
        setPlugins(res.data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const modules = useMemo(() => {
    const out: ModuleEntry[] = [];
    const isAdmin = role === 'admin';
    const permSet = new Set(userPerms ?? []);
    for (const p of plugins) {
      const m = pluginToModule(p);
      if (!m) continue;
      if (dashboardOnly && !m.showOnDashboard) continue;
      if (filterByRole && !canAccessRole(m.requiredRole, role ?? 'guest')) continue;
      // #98 — permission gate. Admin role bypasses (matches backend
      // ROLE_DEFAULT_PERMISSIONS = ['*']). Otherwise require every listed
      // requiredPermissions slug to be in the user's effective set.
      if (
        filterByPermissions &&
        !isAdmin &&
        m.requiredPermissions &&
        m.requiredPermissions.length > 0 &&
        !m.requiredPermissions.every((p) => permSet.has(p))
      ) {
        continue;
      }
      out.push(m);
    }
    out.sort((a, b) => a.dashboardOrder - b.dashboardOrder);
    return out;
  }, [plugins, role, userPerms, filterByRole, filterByPermissions, dashboardOnly]);

  return { modules, loading, error };
}
